"use client";

import { Check, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { useEffect } from "react";
export default function IronPeakElevationSeriesPricing() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  useEffect(() => {
    const calendlyCssHref = "https://assets.calendly.com/assets/external/widget.css";
    const calendlyScriptSrc = "https://assets.calendly.com/assets/external/widget.js";
    if (!document.querySelector(`link[href="${calendlyCssHref}"]`)) {
      const link = document.createElement("link");
      link.href = calendlyCssHref;
      link.rel = "stylesheet";
      document.head.appendChild(link);
    }
    if (!document.querySelector(`script[src="${calendlyScriptSrc}"]`)) {
      const script = document.createElement("script");
      script.src = calendlyScriptSrc;
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);
  const handleScheduleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (typeof window !== "undefined" && (window as any).Calendly) {
      (window as any).Calendly.initPopupWidget({
        url: "https://calendly.com/mfloresdo/30min"
      });
    }
  };
  const tiers = [{
    name: "Basecamp",
    badge: "Recommended Starting Point",
    price: "$150",
    period: "/month",
    description: "Best for men who want physician-led TRT care, clear guidance, and a focused starting point.",
    features: ["Physician-led testosterone evaluation", "Symptom, medical history, and lab review", "Personalized TRT plan when clinically appropriate", "Ongoing monthly support", "Secure messaging and lifestyle guidance"],
    note: "Labs are reviewed as part of care, but lab testing is not included in the Basecamp monthly price.",
    cta: "Book Free Consultation",
    highlight: true
  }, {
    name: "Ascent",
    badge: "Most Common Upgrade",
    price: "$325",
    period: "/month",
    description: "Best for men who want TRT care plus included labs and additional support for recovery, metabolism, and wellness.",
    features: ["Everything in Basecamp", "Lab testing included", "NAD+ therapy when clinically indicated", "Vitamin injections when clinically indicated", "Supplement recommendations", "GLP-1 therapy available when appropriate, billed separately"],
    note: "Supplements are recommended when appropriate but purchased separately.",
    cta: "Book Free Consultation",
    highlight: false
  }, {
    name: "Summit",
    badge: "Highest-Touch Support",
    price: "$595",
    period: "/month",
    description: "Best for men who want the most comprehensive physician-led support, advanced monitoring, and a higher-touch care plan.",
    features: ["Everything in Ascent", "Lab testing included", "Advanced cardiometabolic review", "Higher-touch physician-led follow-up", "NAD+ therapy or vitamin injections when clinically indicated", "Supplement recommendations", "GLP-1 therapy available when appropriate, billed separately"],
    note: "Designed for men who need more comprehensive oversight or a broader metabolic and performance strategy.",
    cta: "Book Free Consultation",
    highlight: false
  }];
  return <section className="py-12 sm:py-16 lg:py-20 bg-background overflow-hidden">
      <div className="container px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-16 max-w-4xl mx-auto">
          <div className="inline-flex max-w-full items-center gap-2 rounded-full border border-primary/30 bg-primary-light-bg px-3 py-1.5 sm:px-4 sm:py-2 mb-5">
            <ShieldCheck className="w-4 h-4 text-primary flex-shrink-0" />
            <span className="text-xs sm:text-sm font-semibold text-primary leading-snug">
              Simple Starting Point. Clear Upgrade Path.
            </span>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4 leading-tight">
            Start With Basecamp TRT. Upgrade Only If Needed.
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Most men begin with Basecamp: physician-led testosterone care for
            qualified Texas patients starting at $150/month. If your goals,
            labs, or medical picture call for more support, Ascent and Summit
            add included labs and additional treatment options when clinically
            appropriate.
          </p>
        </div>

        <div ref={sectionRef} className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {tiers.map((tier, i) => <div key={i} data-index={i} className={`
                min-w-0 transition-all duration-300 ease-out
                ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}
              `} style={{
          transitionDelay: `${i * 100}ms`
        }}>
              <Card className={`
                  h-full flex flex-col relative overflow-hidden
                  border-2 transition-all duration-300 ease-out
                  hover:-translate-y-2 hover:scale-[1.02] hover:shadow-strong
                  ${tier.highlight ? "border-primary shadow-medium bg-primary-light-bg" : "border-muted-light shadow-soft hover:border-primary/50 bg-card"}
                `}>
                {tier.highlight && <div className="absolute top-0 left-0 right-0 bg-primary text-primary-foreground text-center text-xs font-bold py-2">
                    Recommended Starting Point
                  </div>}

                <CardHeader className={tier.highlight ? "pt-12 pb-4" : "pb-4"}>
                  <div className="mb-3">
                    <span className="inline-flex rounded-full bg-background/80 border border-muted-light px-3 py-1 text-xs font-semibold text-primary">
                      {tier.badge}
                    </span>
                  </div>

                  <CardTitle className="text-2xl text-foreground">
                    {tier.name}
                  </CardTitle>

                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                    {tier.description}
                  </p>
                </CardHeader>

                <CardContent className="flex-1 space-y-6">
                  <div className="border-b border-muted-light pb-6">
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl sm:text-5xl font-bold text-foreground">
                        {tier.price}
                      </span>
                      <span className="text-muted-foreground">
                        {tier.period}
                      </span>
                    </div>
                  </div>

                  <ul className="space-y-3">
                    {tier.features.map((feature, featureIndex) => <li key={featureIndex} data-index={featureIndex} className={`
                          flex items-start gap-3 text-sm text-foreground leading-relaxed
                          transition-all duration-150 ease-out
                          ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4"}
                        `} style={{
                  transitionDelay: `${i * 100 + featureIndex * 150}ms`
                }}>
                        <Check className="size-5 text-primary shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>)}
                  </ul>

                  {tier.note && <div className="mt-4 pt-4 border-t border-muted-light">
                      <p className="text-xs text-muted-foreground italic leading-relaxed">
                        {tier.note}
                      </p>
                    </div>}
                </CardContent>

                <CardFooter className="pt-0 flex flex-col gap-3">
                  <Button onClick={handleScheduleClick} variant={tier.highlight ? "default" : "outline"} size="lg" className={`
                      w-full min-h-12 h-auto whitespace-normal text-center leading-snug
                      transition-all duration-200 ease-out px-4 py-3
                      ${tier.highlight ? "bg-primary text-primary-foreground hover:bg-primary-hover hover:scale-105 shadow-soft" : "hover:scale-105 hover:bg-primary-hover hover:text-primary-foreground"}
                    `}>
                    {tier.cta}
                  </Button>
                </CardFooter>
              </Card>
            </div>)}
        </div>

        <div className="mt-10 max-w-5xl mx-auto rounded-xl border border-muted-light bg-card p-5 sm:p-6 md:p-8 shadow-soft">
          <h3 className="text-2xl font-semibold text-foreground mb-4 text-center">
            What’s Included and What’s Billed Separately
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-foreground mb-3">
                Included in Ascent and Summit
              </h4>

              <ul className="space-y-2 text-sm text-muted-foreground leading-relaxed">
                <li className="flex gap-2">
                  <Check className="size-4 text-primary shrink-0 mt-0.5" />
                  <span>Lab testing included</span>
                </li>
                <li className="flex gap-2">
                  <Check className="size-4 text-primary shrink-0 mt-0.5" />
                  <span>NAD+ therapy when clinically indicated</span>
                </li>
                <li className="flex gap-2">
                  <Check className="size-4 text-primary shrink-0 mt-0.5" />
                  <span>Vitamin injections when clinically indicated</span>
                </li>
                <li className="flex gap-2">
                  <Check className="size-4 text-primary shrink-0 mt-0.5" />
                  <span>Supplement recommendations</span>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-foreground mb-3">
                Billed Separately When Used
              </h4>

              <ul className="space-y-2 text-sm text-muted-foreground leading-relaxed">
                <li className="flex gap-2">
                  <Check className="size-4 text-primary shrink-0 mt-0.5" />
                  <span>
                    GLP-1 medications, available only in Ascent or Summit when
                    medically appropriate
                  </span>
                </li>
                <li className="flex gap-2">
                  <Check className="size-4 text-primary shrink-0 mt-0.5" />
                  <span>Supplements you choose to purchase</span>
                </li>
                <li className="flex gap-2">
                  <Check className="size-4 text-primary shrink-0 mt-0.5" />
                  <span>
                    Services, medications, or testing outside the selected
                    package
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <p className="text-xs text-muted-foreground italic text-center mt-6 max-w-4xl mx-auto leading-relaxed">
            Treatment options are based on physician review and are prescribed
            only when clinically appropriate.
          </p>
        </div>
      </div>
    </section>;
}